import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

/**
 * Der Beitritt eines Handys.
 *
 * Liegt bewusst im localStorage: Ein Handy sperrt sich im Unterricht ständig,
 * der Browser wird weggewischt und wieder geöffnet, das WLAN bricht ab. Ohne
 * gespeichertes Token müsste die Schülerin jedes Mal neu beitreten - und
 * bekäme dabei einen zweiten Teilnehmer-Datensatz, der die Auswertung
 * verfälscht.
 *
 * Gespeichert wird nur, was zum Wiedereinstieg nötig ist. Ein Klarname ist
 * nicht dabei, weil es im ganzen System keinen gibt.
 */
export interface SessionState {
    token: string | null
    nickname: string | null
    joinCode: string | null
    participantId: number | null
    join: (payload: {
        token: string
        nickname: string
        joinCode: string
        participantId: number
    }) => void
    leave: () => void
}

export const useSessionStore = create<SessionState>()(
    persist(
        (set) => ({
            token: null,
            nickname: null,
            joinCode: null,
            participantId: null,
            join: (payload) => set({ ...payload }),
            leave: () => set({ token: null, nickname: null, joinCode: null, participantId: null }),
        }),
        {
            name: 'wb_token',
            storage: createJSONStorage(() => safeStorage()),
        },
    ),
)

/**
 * localStorage, wenn es benutzbar ist - sonst ein Speicher, der nur im
 * Arbeitsspeicher lebt.
 *
 * Im privaten Modus mancher Browser wirft schon der Zugriff auf localStorage.
 * Ohne diesen Fallback käme die Schülerin gar nicht in die Runde. Mit ihm
 * verliert sie nur den Wiedereinstieg nach einem Neuladen - die Stunde läuft
 * weiter.
 */
function safeStorage(): Storage {
    try {
        const probe = '__wettbandit__'

        globalThis.localStorage.setItem(probe, probe)
        globalThis.localStorage.removeItem(probe)

        return globalThis.localStorage
    } catch {
        return memoryStorage
    }
}

const memoryStorage: Storage = (() => {
    const entries = new Map<string, string>()

    return {
        get length() {
            return entries.size
        },
        clear: () => entries.clear(),
        getItem: (key) => entries.get(key) ?? null,
        key: (index) => [...entries.keys()][index] ?? null,
        removeItem: (key) => entries.delete(key),
        setItem: (key, value) => entries.set(key, value),
    }
})()

/** Das Token außerhalb von React - der API-Client ist keine Komponente. */
export function currentToken(): string | null {
    return useSessionStore.getState().token
}
